/**
 * Editor API Service
 * Backend와 통신하여 파일 파싱/내보내기 처리
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

/**
 * 파일 확장자로 파일 타입 결정
 * @param {string} fileName
 * @returns {'svg' | 'ppt' | 'psd' | null}
 */
export const getFileType = (fileName) => {
  const ext = fileName.toLowerCase().split('.').pop();

  if (ext === 'svg') return 'svg';
  if (['ppt', 'pptx'].includes(ext)) return 'ppt';
  if (ext === 'psd') return 'psd';
  if (ext === 'pdf') return 'pdf';

  return null;
};

/**
 * SVG 파일 파싱
 * @param {File} file
 * @param {string} userId
 * @returns {Promise<{success: boolean, doc_id?: string, canvas_size?: [number, number], error?: string}>}
 */
export const parseSvgFile = async (file, userId = null) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    if (userId) formData.append('userId', userId);

    const response = await fetch(`${API_BASE_URL}/editor/parse_svg`, {
      method: 'POST',
      body: formData,
    });

    return await response.json();
  } catch (error) {
    console.error('SVG 파싱 오류:', error);
    return { success: false, error: error.message };
  }
};

/**
 * PPT 파일 파싱
 * @param {File} file
 * @param {string} userId
 * @returns {Promise<{success: boolean, doc_id?: string, canvas_size?: [number, number], error?: string}>}
 */
export const parsePptFile = async (file, userId = null) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    if (userId) formData.append('userId', userId);

    const response = await fetch(`${API_BASE_URL}/editor/parse_ppt`, {
      method: 'POST',
      body: formData,
    });

    return await response.json();
  } catch (error) {
    console.error('PPT 파싱 오류:', error);
    return { success: false, error: error.message };
  }
};

/**
 * PSD 파일 파싱
 * @param {File} file
 * @param {string} userId
 * @returns {Promise<{success: boolean, doc_id?: string, canvas_size?: [number, number], error?: string}>}
 */
export const parsePsdFile = async (file, userId = null) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    if (userId) formData.append('userId', userId);

    const response = await fetch(`${API_BASE_URL}/editor/parse_psd`, {
      method: 'POST',
      body: formData,
    });

    return await response.json();
  } catch (error) {
    console.error('PSD 파싱 오류:', error);
    return { success: false, error: error.message };
  }
};

/**
 * 파일 타입에 따라 적절한 파서 호출
 * @param {File} file
 * @param {string} userId
 * @returns {Promise<{success: boolean, doc_id?: string, canvas_size?: [number, number], error?: string}>}
 */
/**
 * PDF 파일 파싱
 * @param {File} file
 * @param {string} userId
 * @returns {Promise<{success: boolean, doc_id?: string, canvas_size?: [number, number], error?: string}>}
 */
export const parsePdfFile = async (file, userId = null) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    if (userId) formData.append('userId', userId);

    const response = await fetch(`${API_BASE_URL}/editor/parse_pdf`, {
      method: 'POST',
      body: formData,
    });

    return await response.json();
  } catch (error) {
    console.error('PDF 파싱 오류:', error);
    return { success: false, error: error.message };
  }
};

export const parseFile = async (file, userId = null) => {
  const fileType = getFileType(file.name);

  switch (fileType) {
    case 'svg':
      return parseSvgFile(file, userId);
    case 'ppt':
      return parsePptFile(file, userId);
    case 'psd':
      return parsePsdFile(file, userId);
    case 'pdf':
      return parsePdfFile(file, userId);
    default:
      return { success: false, error: '지원하지 않는 파일 형식입니다. (SVG, PPT, PPTX, PSD, PDF)' };
  }
};

/**
 * PPT로 내보내기 (doc_id 기반)
 * @param {string} docId
 * @returns {Promise<Blob>}
 */
export const exportPptFromDoc = async (docId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/editor/export_ppt`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ doc_id: docId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'PPT 내보내기 실패');
    }

    return await response.blob();
  } catch (error) {
    console.error('PPT 내보내기 오류:', error);
    throw error;
  }
};

/**
 * PPT로 내보내기 (직접 데이터 전달)
 * @param {Object} data - { canvas_size: [w, h], data_list: [[layer, ...], ...] }
 * @returns {Promise<Blob>}
 */
export const exportPptFromData = async (data) => {
  try {
    const response = await fetch(`${API_BASE_URL}/editor/export_ppt`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'PPT 내보내기 실패');
    }

    return await response.blob();
  } catch (error) {
    console.error('PPT 내보내기 오류:', error);
    throw error;
  }
};

/**
 * Blob을 파일로 다운로드
 * @param {Blob} blob
 * @param {string} fileName
 */
export const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * 프로젝트 다중 삭제 (Firestore + Storage)
 * @param {string[]} projectIds
 */
export const deleteProjects = async (projectIds) => {
  try {
    const response = await fetch(`${API_BASE_URL}/editor/delete_projects`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ project_ids: projectIds }),
    });
    return await response.json();
  } catch (error) {
    console.error('프로젝트 삭제 오류:', error);
    return { success: false, error: error.message };
  }
};

/**
 * 템플릿 다중 삭제 (Firestore + Storage)
 * @param {string[]} docIds
 */
export const deleteTemplates = async (docIds) => {
  try {
    const response = await fetch(`${API_BASE_URL}/editor/delete_templates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ doc_ids: docIds }),
    });
    return await response.json();
  } catch (error) {
    console.error('템플릿 삭제 오류:', error);
    return { success: false, error: error.message };
  }
};
